import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { removeTypename } from "../../../../api";
import { Loading } from "../../../../components";
import Editor from "../../../../components/editor/Editor";
import Integrations from "../../../../components/pickers/integrations/Integrations";
import RequestTypes from "../../../../components/pickers/request-types/RequestTypes";
import Select from "../../../../components/select/Select";
import useRouting, { useParams } from "../../../../contexts/Routing";
import {
    ChatSettings,
    ChatSettingsChannelMenu, ChatSettingsDeflection, ChatSettingsInput, ChatSettingsSlack, GetChatSettingsDocument, GetSlackChannelSettingsDocument, IntegrationActionType, Settings,
    useGetChatSettingsQuery, useSaveChatSettingsMutation,
} from "../../../../__generated__/api";
import { ErrorRemote } from "../../../error/Error";
import { SettingsProps } from "../base";
import WelcomeMessageHelp from "../channels/slack/WelcomeMessageHelp";
import ChannelMenuSettings from "./settings/ChannelMenu";
import DeflectionSettings from "./settings/Deflection";
import SlackSettings from "./settings/Slack";

export function DisplayChatSettings({
    chatSettings, settings, onChange, shouldAllowSlack, shouldAllowAutomation,
}: {
    chatSettings: ChatSettings | undefined;
    settings: Settings | undefined;
    onChange: (input: ChatSettingsInput) => void;
    shouldAllowSlack?: boolean;
    shouldAllowAutomation?: boolean;
}): ReactElement {
    const [welcomeMessage, setWelcomeMessaage] = useState(() => chatSettings?.welcomeMessage?.rawText ?? "");
    const [identities, setIdentities] = useState<string[]>(() => chatSettings?.identities ?? []);
    const [formIds, setFormIds] = useState<string[]>(() => chatSettings?.formIds ?? []);
    const [slaTierId, setSlaTierId] = useState<string>(() => chatSettings?.slaTierId ?? undefined);

    const [channelMenu, setChannelMenu] = useState(() => chatSettings?.options?.channelMenu);

    const [slack, setSlack] = useState(() => chatSettings?.options?.slack);
    const [deflection, setDeflection] = useState(() => chatSettings?.options?.deflection);

    const [state, setState] = useState<ChatSettingsInput>();

    useEffect(() => {
        if (!chatSettings || chatSettings === state) {
            return;
        }
        setWelcomeMessaage(chatSettings.welcomeMessage?.rawText ?? "");
        setIdentities(chatSettings.identities ?? []);
        setFormIds(chatSettings.formIds ?? []);
        setChannelMenu(chatSettings.options?.channelMenu);
        setSlack(chatSettings.options?.slack);
        setDeflection(chatSettings.options?.deflection);
        setSlaTierId(chatSettings.slaTierId);
    }, [chatSettings]);

    type TypeName = Parameters<typeof removeTypename>[0];

    useEffect(() => {
        const value: ChatSettingsInput = removeTypename<ChatSettingsInput & TypeName>({
            welcomeMessage: welcomeMessage ? { rawText: welcomeMessage } : undefined,
            identities,
            formIds,
            slaTierId,
            options: {
                channelMenu,
                slack,
                deflection,
            },
        } as TypeName);

        setState(value);
        onChange(value);
    }, [welcomeMessage, identities, formIds, slaTierId, channelMenu, slack, deflection]);

    const currentSlaTier = slaTierId ? settings?.sla?.tiers?.find(tier => tier.id === slaTierId) : undefined;

    return (
        <div>
            <div className="border p-3 rounded bg-white">
                <h5 className="mb-3 text-light">
                    Chat Settings
                </h5>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">
                        Welcome Message
                        {" "}
                        <WelcomeMessageHelp />
                    </Form.Label>
                    <div className="bg-white border-light border rounded">
                        <Editor
                            text={welcomeMessage as string}
                            onChange={msg => {
                                setWelcomeMessaage(msg);
                            }}
                            shouldFocus
                            placeholder="Hey, please remember to use a thread..."
                        />
                    </div>
                    <Form.Text>
                        When a customer first asks a question, this is the message they will
                        first see in this channel. If you want to overwrite with no message,
                        make the defaut message &quot;none&quot;.
                        {" "}
                        <WelcomeMessageHelp />
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Ticket Integration</Form.Label>
                    <Integrations
                        placeholder="Which integration should create a ticket"
                        onSelected={selected => {
                            setIdentities(selected ? [selected] : []);
                        }}
                        isClearable
                        value={identities.find(identity => identity.startsWith("urn:fq:integration"))}
                        actions={[IntegrationActionType.CreateTicket]}
                    />
                    <Form.Text>
                        When creating a new ticket, Agent can automatically create a ticket in a 3rd party
                        services such as Zendesk, Intercom, Atlassian, or something else. Which existing
                        integration would you like to use for this?

                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Request Types</Form.Label>
                    <RequestTypes
                        isMulti
                        placeholder="Which service should be selected"
                        onSelected={selected => {
                            setFormIds(selected);
                        }}
                        value={formIds}
                    />
                    <Form.Text>
                        When a user asks a question, thse are the request types the user can fill out.
                        To order the request types, click and drag them inside this box.
                    </Form.Text>
                </Form.Group>

                {settings?.sla?.tiers?.isNotEmpty && (
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">SLA Tier</Form.Label>
                        <Select
                            options={settings?.sla?.tiers?.map(tier => ({
                                label: tier.name as string,
                                value: tier.id as string,
                                data: tier,
                            })) ?? []}
                            isClearable
                            onChange={tier => {
                                setSlaTierId(tier?.data?.id);
                            }}
                            value={!currentSlaTier ? undefined : {
                                label: currentSlaTier.name as string,
                                value: currentSlaTier.id as string,
                                data: currentSlaTier,
                            }}
                        />
                        <Form.Text>
                            Which SLA tier should apply to this channel?
                        </Form.Text>
                    </Form.Group>
                )}

            </div>

            {(slack || shouldAllowSlack) && (
                <SlackSettings
                    data={slack as ChatSettingsSlack}
                    shouldAllowAutomation={shouldAllowAutomation}
                    canAddTags={chatSettings?.options?.tag == null}
                    onChange={value => {
                        setSlack(value);
                    }}
                />
            )}

            <ChannelMenuSettings
                data={channelMenu as ChatSettingsChannelMenu | undefined}
                onChange={value => {
                    setChannelMenu(value);
                }}
            />

            <DeflectionSettings
                data={deflection as ChatSettingsDeflection}
                onChange={value => {
                    setDeflection(value);
                }}
            />

        </div>
    );
}
DisplayChatSettings.defaultProps = {
    shouldAllowSlack: false,
    shouldAllowAutomation: false,
};

export default function Chat(props: SettingsProps): ReactElement {
    const { Paths, historyPush } = useRouting();
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const [save] = useSaveChatSettingsMutation();

    const { data, error, loading: isLoading } = useGetChatSettingsQuery({
        fetchPolicy: "no-cache",
        variables: {
            id,
        },
    });
    const [settings, setSettings] = useState<ChatSettingsInput>();

    useEffect(() => {
        if (data?.chatSettings) {
            setSettings(data.chatSettings);
        }
    }, [data?.chatSettings]);

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (!settings) {
            return;
        }
        (async () => {
            await save({
                refetchQueries: [
                    { query: GetSlackChannelSettingsDocument },
                    { query: GetChatSettingsDocument, variables: { id } },
                ],
                variables: {
                    id,
                    settings,
                },
            });

            historyPush(Paths.ADMINISTRATOR_SETTING, {
                setting: "channels-slack",
            });
        })();
    };

    return (
        <form className="flex-grow-1" onSubmit={onSubmit}>

            <DisplayChatSettings
                chatSettings={data?.chatSettings as ChatSettings}
                settings={props.settings}
                onChange={setSettings}
                shouldAllowAutomation
            />

            <div className="d-flex justify-content-end mt-3 fixed-bottom border-top p-3 bg-white">
                <button type="submit" className="btn btn-primary">
                    Save
                </button>
            </div>
        </form>
    );
}
